import debounce from 'lodash.debounce';
import { useRef, useMemo, useEffect, useCallback } from 'react';
import {
  Icon,
  BadgeColor,
  useNavigate,
  UsersBadgePopup,
  isMobile as isMobileHook,
} from '@gv/triage-components';

import { useAblyEvent } from 'hooks';
import { useAppSelector } from 'store';
import { Config, Routes } from 'config';
import { selectIsAdmin } from 'store/slices/auth';
import { useLazyOnlineQuery } from 'store/api/users';
import { joinRoutes, getCurrentUserStatus } from 'utils/helpers';
import { NurseStatusTitle, NurseStatusBadgeColor } from 'types/data';

const { Home, List } = Routes.Team;

const badgeAllowed = Config.portalType.isTeam;
const analyticsPath = joinRoutes([Home, List.Analytics], {});

export const useOnlineBadge = () => {
  const isMobile = isMobileHook();
  const navigate = useNavigate();

  // Don't show team online status in Chrome extension
  const isChromeExtension = process.env.REACT_APP_CHROME_EXTENSION === 'true';

  const [trigger, { data }] = useLazyOnlineQuery();

  const lastRequest = useRef<ReturnType<typeof trigger> | null>(null);

  const refetch = useCallback(() => {
    if (!badgeAllowed || isChromeExtension) {
      return;
    }
    lastRequest.current?.abort();
    const req = trigger({ page: 1, limit: 5 });
    lastRequest.current = req;
  }, [isChromeExtension]);

  useEffect(() => {
    if (!isChromeExtension) {
      refetch();
    }
  }, [refetch, isChromeExtension]);

  useEffect(() => {
    if (isChromeExtension) {
      return;
    }
    refetch(); // Initial call on mount
    const intervalId = setInterval(refetch, 5000); // 5 seconds

    return () => clearInterval(intervalId); // Cleanup interval on unmount
  }, [refetch, isChromeExtension]);

  const debounceRefetch = useMemo(() => debounce(refetch, 1500), [refetch]);

  const isAdmin = useAppSelector(selectIsAdmin);

  useEffect(() => {
    return () => debounceRefetch.cancel();
  }, [debounceRefetch]);

  useAblyEvent(
    badgeAllowed && !isChromeExtension
      ? ['online-status-changed', 'user-status-changed']
      : undefined,
    debounceRefetch
  );

  const currentTasks = useAppSelector((store) => store?.actionQueue?.entities);

  const badges = useMemo(() => {
    // Don't show team online status in Chrome extension
    if (isChromeExtension) {
      return [];
    }
    if (!data || !badgeAllowed) {
      return [];
    }
    const { count, users } = data;
    if (count === 0) {
      return [];
    }
    if (isMobile) {
      return [
        {
          count,
          icon: <Icon.Team />,
          styleType: BadgeColor.Blue,
          onClick: isAdmin ? () => navigate(analyticsPath) : undefined,
        },
      ];
    }
    const usersList = users.map((user) => {
      const { status } = user;
      const userStatus = getCurrentUserStatus(status);

      return {
        id: user.id,
        name: user.name,
        status: userStatus,
        role: user.role.name,
        imageUrl: user.profile_pic,
        badgeText: NurseStatusTitle[status!],
        badgeColor: NurseStatusBadgeColor[status!],
      };
    });
    return [
      {
        count,
        text: 'Team online',
        styleType: BadgeColor.Blue,
        popup: (
          <UsersBadgePopup
            users={usersList}
            viewAllLink={isAdmin ? analyticsPath : undefined}
          />
        ),
      },
    ];
  }, [data, isMobile, currentTasks, isChromeExtension, isAdmin, navigate]);

  return badges;
};
